import React, { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/Card'
import { Switch } from 'components/ui/switch'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Label } from 'components/ui/Label'
import { useTranslation } from 'react-i18next'
import { useToast } from 'contexts/ToastContext'
import { notificationsApi, type NotificationPreference } from 'lib/api'
import { 
  Bell, 
  Mail, 
  Smartphone, 
  Moon, 
  Clock,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { cn } from 'lib/utils'

export function NotificationPreferences() {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [preferences, setPreferences] = useState<NotificationPreference | null>(null)

  // Load preferences
  const loadPreferences = useCallback(async () => {
    try {
      setLoading(true)
      const response = await notificationsApi.getPreferences()
      setPreferences(response.data)
    } catch (error: any) {
      console.error('Failed to load notification preferences:', error)
      addToast({
        type: 'error',
        message: t('settings.notifications.loadError', 'Failed to load notification preferences')
      })
    } finally {
      setLoading(false)
    }
  }, [t, addToast])

  useEffect(() => {
    loadPreferences()
  }, [loadPreferences])

  // Save preferences
  const savePreferences = async () => {
    if (!preferences) return

    try {
      setSaving(true)
      await notificationsApi.updatePreferences(preferences)
      addToast({
        type: 'success',
        message: t('settings.notifications.saveSuccess', 'Notification preferences saved successfully')
      })
    } catch (error: any) {
      console.error('Failed to save notification preferences:', error)
      addToast({
        type: 'error',
        message: t('settings.notifications.saveError', 'Failed to save notification preferences')
      })
    } finally {
      setSaving(false)
    }
  }

  // Update preference field
  const updateField = (field: keyof NotificationPreference, value: any) => {
    if (!preferences) return
    setPreferences({ ...preferences, [field]: value })
  }

  // Update category preference
  const updateCategoryPreference = (category: string, setting: string, value: boolean) => {
    if (!preferences) return
    const categoryPrefs = { ...preferences.category_preferences }
    if (!categoryPrefs[category]) {
      categoryPrefs[category] = {}
    }
    categoryPrefs[category] = { ...categoryPrefs[category], [setting]: value }
    setPreferences({ ...preferences, category_preferences: categoryPrefs })
  }

  // Get category preference
  const getCategoryPreference = (category: string, setting: string): boolean => {
    if (!preferences) return true
    return preferences.category_preferences[category]?.[setting] ?? true
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    )
  }

  if (!preferences) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {t('settings.notifications.loadError', 'Failed to load notification preferences')}
            </p>
            <Button onClick={loadPreferences} className="mt-4" variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('common.retry', 'Retry')}
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  const categories = [
    { key: 'cv', label: t('settings.notifications.categories.cv', 'CV Imports') },
    { key: 'matching', label: t('settings.notifications.categories.matching', 'AI Matching') },
    { key: 'offer', label: t('settings.notifications.categories.offer', 'Job Offers') },
    { key: 'process', label: t('settings.notifications.categories.process', 'Hiring Process') },
    { key: 'application', label: t('settings.notifications.categories.application', 'Applications') },
  ]

  return (
    <div className="space-y-6">
      {/* Global Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            {t('settings.notifications.globalSettings', 'Global Notification Settings')}
          </CardTitle>
          <CardDescription>
            {t('settings.notifications.globalSettingsDesc', 'Control how you receive notifications across all categories')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-gray-500" />
              <div>
                <Label className="text-base font-medium">
                  {t('settings.notifications.emailNotifications', 'Email Notifications')}
                </Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.notifications.emailNotificationsDesc', 'Receive notifications via email')}
                </p>
              </div>
            </div>
            <Switch
              checked={preferences.email_enabled}
              onCheckedChange={(checked) => updateField('email_enabled', checked)}
            />
          </div>

          {/* SMS Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Smartphone className="h-5 w-5 text-gray-500" />
              <div>
                <Label className="text-base font-medium">
                  {t('settings.notifications.smsNotifications', 'SMS Notifications')}
                </Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.notifications.smsNotificationsDesc', 'Receive notifications via SMS')}
                </p>
              </div>
            </div>
            <Switch
              checked={preferences.sms_enabled}
              onCheckedChange={(checked) => updateField('sms_enabled', checked)}
            />
          </div>

          {/* Push Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="h-5 w-5 text-gray-500" />
              <div>
                <Label className="text-base font-medium">
                  {t('settings.notifications.pushNotifications', 'Push Notifications')}
                </Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.notifications.pushNotificationsDesc', 'Receive in-app push notifications')}
                </p>
              </div>
            </div>
            <Switch
              checked={preferences.push_enabled}
              onCheckedChange={(checked) => updateField('push_enabled', checked)}
            />
          </div>

          {/* Browser Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="h-5 w-5 text-gray-500" />
              <div>
                <Label className="text-base font-medium">
                  {t('settings.notifications.browserNotifications', 'Browser Notifications')}
                </Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.notifications.browserNotificationsDesc', 'Receive browser desktop notifications')}
                </p>
              </div>
            </div>
            <Switch
              checked={preferences.browser_notifications_enabled}
              onCheckedChange={(checked) => updateField('browser_notifications_enabled', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Quiet Hours */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Moon className="h-5 w-5" />
            {t('settings.notifications.quietHours', 'Quiet Hours')}
          </CardTitle>
          <CardDescription>
            {t('settings.notifications.quietHoursDesc', 'Pause notifications during specific hours')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium">
              {t('settings.notifications.enableQuietHours', 'Enable Quiet Hours')}
            </Label>
            <Switch
              checked={preferences.quiet_hours_enabled}
              onCheckedChange={(checked) => updateField('quiet_hours_enabled', checked)}
            />
          </div>

          {preferences.quiet_hours_enabled && (
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <Label htmlFor="quiet-hours-start" className="mb-2 block">
                  {t('settings.notifications.quietHoursStart', 'Start Time')}
                </Label>
                <Input
                  id="quiet-hours-start"
                  type="time"
                  value={preferences.quiet_hours_start || '22:00'}
                  onChange={(e) => updateField('quiet_hours_start', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="quiet-hours-end" className="mb-2 block">
                  {t('settings.notifications.quietHoursEnd', 'End Time')}
                </Label>
                <Input
                  id="quiet-hours-end"
                  type="time"
                  value={preferences.quiet_hours_end || '08:00'}
                  onChange={(e) => updateField('quiet_hours_end', e.target.value)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Category Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            {t('settings.notifications.categoryPreferences', 'Category Preferences')}
          </CardTitle>
          <CardDescription>
            {t('settings.notifications.categoryPreferencesDesc', 'Customize notifications for each category')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {categories.map((category) => (
            <div key={category.key} className="space-y-3 pb-4 border-b last:border-0">
              <h4 className="font-medium text-base">{category.label}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">
                    {t('settings.notifications.email', 'Email')}
                  </Label>
                  <Switch
                    checked={getCategoryPreference(category.key, 'email')}
                    onCheckedChange={(checked) => updateCategoryPreference(category.key, 'email', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">
                    {t('settings.notifications.push', 'Push')}
                  </Label>
                  <Switch
                    checked={getCategoryPreference(category.key, 'push')}
                    onCheckedChange={(checked) => updateCategoryPreference(category.key, 'push', checked)}
                  />
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button
          onClick={loadPreferences}
          variant="outline"
          disabled={saving}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('common.reset', 'Reset')}
        </Button>
        <Button
          onClick={savePreferences}
          disabled={saving}
          className="min-w-[120px]"
        >
          {saving ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              {t('common.saving', 'Saving...')}
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              {t('common.save', 'Save')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

